/**
 * Centralized URL generation for appraiser sites.
 * Replaces all scattered `/site/${subdomain}/...` references.
 *
 * In production, generates subdomain-based URLs: https://smith.ashlarapp.com/about
 * In dev/preview, falls back to path-based URLs: /site/smith/about
 */

const APP_DOMAIN = 'ashlarapp.com';

/**
 * Global flag set by SubdomainSiteRouter to indicate we're rendering
 * a subdomain-served site (works on custom domains, etc.)
 */
let _isSubdomainContext = false;

export function setSubdomainContext(active: boolean) {
  _isSubdomainContext = active;
}

/** Whether the current environment supports subdomain routing */
function isSubdomainEnvironment(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.endsWith(`.${APP_DOMAIN}`) ||
         window.location.hostname === APP_DOMAIN;
}

/** Whether we're currently ON a subdomain site */
function isOnSubdomain(): boolean {
  // If SubdomainSiteRouter has flagged us, trust that
  if (_isSubdomainContext) return true;

  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  if (!hostname.endsWith(`.${APP_DOMAIN}`)) return false;
  const sub = hostname.slice(0, -(APP_DOMAIN.length + 1));
  return !!sub && !sub.includes('.');
}

/**
 * Get an absolute URL for a site page.
 * Always uses subdomain format in production; path-based in dev.
 *
 * @example getSiteUrl('smith', '/about') → "https://smith.ashlarapp.com/about"
 */
export function getSiteUrl(subdomain: string, path: string = '/'): string {
  if (isSubdomainEnvironment()) {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `https://${subdomain}.${APP_DOMAIN}${cleanPath === '/' ? '' : cleanPath}`;
  }
  // Dev/preview fallback
  const cleanPath = path === '/' ? '' : path.startsWith('/') ? path : `/${path}`;
  return `/site/${subdomain}${cleanPath}`;
}

/**
 * Get a root-relative path for internal <Link> navigation.
 * When on a subdomain, returns root-relative paths (/about).
 * When on the main domain (or dev), returns path-based (/site/smith/about).
 *
 * @example getSitePath('smith', '/about')
 *   On subdomain: "/about"
 *   On main domain: "/site/smith/about"
 */
export function getSitePath(subdomain: string, path: string = '/'): string {
  if (isOnSubdomain()) {
    return path.startsWith('/') ? path : `/${path}`;
  }
  // Main domain or dev: use path-based routing
  const cleanPath = path === '/' ? '' : path.startsWith('/') ? path : `/${path}`;
  return `/site/${subdomain}${cleanPath}`;
}

/**
 * Get the canonical URL for SEO purposes.
 * Always returns the absolute subdomain URL regardless of current environment.
 *
 * @example getCanonicalSiteUrl('smith', '/about') → "https://smith.ashlarapp.com/about"
 */
export function getCanonicalSiteUrl(subdomain: string, path: string = '/'): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `https://${subdomain}.${APP_DOMAIN}${cleanPath === '/' ? '' : cleanPath}`;
}

/**
 * Get the base URL for a site (no trailing slash).
 * @example getSiteBaseUrl('smith') → "https://smith.ashlarapp.com"
 */
export function getSiteBaseUrl(subdomain: string): string {
  return `https://${subdomain}.${APP_DOMAIN}`;
}
